import { useState, useEffect, useRef } from 'react';
import {
  Moon,
  Music,
  Clock,
  TrendingUp,
  Trash2,
  Plus,
  GripVertical,
  Eye,
  EyeOff,
  Save,
  Image as ImageIcon,
  BarChart2,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import type { SleepSound } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';

const supabase = getSupabaseAdminClient();

// ============================================
// TYPES
// ============================================

interface KPIStats {
  thisMonthMinutes: number;
  thisYearMinutes: number;
  mostPlayed: { title_de: string; title_en: string; count: number } | null;
}

interface SoundFormData {
  title_de: string;
  title_en: string;
  soundFile: File | null;
  imageFile: File | null;
}

// ============================================
// KPI CARD
// ============================================

interface KPICardProps {
  title: string;
  value: string;
  icon: typeof Moon;
  color: string;
  subtitle?: string;
}

function KPICard({ title, value, icon: Icon, color, subtitle }: KPICardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

// ============================================
// HELPER: Format minutes
// ============================================
function formatDuration(totalMinutes: number): string {
  if (totalMinutes < 60) return `${totalMinutes} Min`;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (mins === 0) return `${hours} Std`;
  return `${hours} Std ${mins} Min`;
}

// ============================================
// MAIN PAGE
// ============================================

export function SleepSoundsAdminPage() {
  const [sounds, setSounds] = useState<SleepSound[]>([]);
  const [kpis, setKpis] = useState<KPIStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<SoundFormData>({
    title_de: '',
    title_en: '',
    soundFile: null,
    imageFile: null,
  });
  const [isUploading, setIsUploading] = useState(false);
  const soundInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([fetchSounds(), fetchKPIs()]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSounds = async () => {
    const { data, error } = await supabase
      .from('sleep_sounds')
      .select('*')
      .order('sort_order', { ascending: true });
    if (error) {
      console.error('Error fetching sounds:', error);
      return;
    }
    setSounds((data as SleepSound[]) || []);
  };

  const fetchKPIs = async () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const startOfYear = new Date(now.getFullYear(), 0, 1).toISOString();

    // This month
    const { data: monthData } = await supabase
      .from('sleep_sound_sessions')
      .select('actual_duration_seconds')
      .gte('started_at', startOfMonth)
      .not('actual_duration_seconds', 'is', null);

    const thisMonthSeconds = (monthData as { actual_duration_seconds: number }[] | null)?.reduce(
      (sum, s) => sum + (s.actual_duration_seconds || 0),
      0
    ) || 0;

    // This year
    const { data: yearData } = await supabase
      .from('sleep_sound_sessions')
      .select('actual_duration_seconds')
      .gte('started_at', startOfYear)
      .not('actual_duration_seconds', 'is', null);

    const thisYearSeconds = (yearData as { actual_duration_seconds: number }[] | null)?.reduce(
      (sum, s) => sum + (s.actual_duration_seconds || 0),
      0
    ) || 0;

    // Most played sound
    const { data: sessionsData } = await supabase
      .from('sleep_sound_sessions')
      .select('sleep_sound_id');

    const countMap: Record<string, number> = {};
    (sessionsData as { sleep_sound_id: string }[] | null)?.forEach((s) => {
      countMap[s.sleep_sound_id] = (countMap[s.sleep_sound_id] || 0) + 1;
    });

    let mostPlayedId: string | null = null;
    let maxCount = 0;
    for (const [id, count] of Object.entries(countMap)) {
      if (count > maxCount) {
        maxCount = count;
        mostPlayedId = id;
      }
    }

    let mostPlayed: KPIStats['mostPlayed'] = null;
    if (mostPlayedId) {
      const { data: soundData } = await supabase
        .from('sleep_sounds')
        .select('title_de, title_en')
        .eq('id', mostPlayedId)
        .single();
      if (soundData) {
        const sd = soundData as { title_de: string; title_en: string };
        mostPlayed = { title_de: sd.title_de, title_en: sd.title_en, count: maxCount };
      }
    }

    setKpis({
      thisMonthMinutes: Math.round(thisMonthSeconds / 60),
      thisYearMinutes: Math.round(thisYearSeconds / 60),
      mostPlayed,
    });
  };

  const handleAddSound = async () => {
    if (!formData.title_de || !formData.title_en || !formData.soundFile || !formData.imageFile) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }

    setIsUploading(true);
    try {
      const timestamp = Date.now();

      // Upload sound file
      const soundExt = formData.soundFile.name.split('.').pop();
      const soundPath = `${timestamp}_${formData.title_de.replace(/\s+/g, '_').toLowerCase()}.${soundExt}`;
      const { error: soundUploadError } = await supabase.storage
        .from('SleepSounds')
        .upload(soundPath, formData.soundFile, { upsert: true });
      if (soundUploadError) throw soundUploadError;

      // Upload image file
      const imageExt = formData.imageFile.name.split('.').pop();
      const imagePath = `${timestamp}_${formData.title_de.replace(/\s+/g, '_').toLowerCase()}.${imageExt}`;
      const { error: imageUploadError } = await supabase.storage
        .from('SleepSoundImages')
        .upload(imagePath, formData.imageFile, { upsert: true });
      if (imageUploadError) throw imageUploadError;

      // Insert DB record
      const maxSort = sounds.length > 0 ? Math.max(...sounds.map((s) => s.sort_order)) : -1;
      const { error: insertError } = await (supabase.from('sleep_sounds') as any).insert({
        title_de: formData.title_de,
        title_en: formData.title_en,
        sound_path: soundPath,
        image_path: imagePath,
        sort_order: maxSort + 1,
      });
      if (insertError) throw insertError;

      toast.success('Sound hinzugefügt');
      setFormData({ title_de: '', title_en: '', soundFile: null, imageFile: null });
      setShowAddForm(false);
      await fetchSounds();
    } catch (err) {
      console.error('Error adding sound:', err);
      toast.error('Fehler beim Hinzufügen');
    } finally {
      setIsUploading(false);
    }
  };

  const handleToggleActive = async (sound: SleepSound) => {
    const { error } = await (supabase.from('sleep_sounds') as any)
      .update({ is_active: !sound.is_active, updated_at: new Date().toISOString() })
      .eq('id', sound.id);
    if (error) {
      toast.error('Fehler beim Ändern');
      return;
    }
    toast.success(sound.is_active ? 'Deaktiviert' : 'Aktiviert');
    await fetchSounds();
  };

  const handleDelete = async (sound: SleepSound) => {
    if (!confirm(`"${sound.title_de}" wirklich löschen?`)) return;

    try {
      // Delete storage files
      await supabase.storage.from('SleepSounds').remove([sound.sound_path]);
      await supabase.storage.from('SleepSoundImages').remove([sound.image_path]);

      // Delete DB record
      const { error } = await supabase.from('sleep_sounds').delete().eq('id', sound.id);
      if (error) throw error;

      toast.success('Sound gelöscht');
      await fetchSounds();
    } catch (err) {
      console.error('Error deleting sound:', err);
      toast.error('Fehler beim Löschen');
    }
  };

  const getPublicUrl = (bucket: string, path: string) => {
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schlafklänge</h1>
          <p className="text-gray-500 mt-1">Sleep Sounds verwalten</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Sound hinzufügen
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard
          title="Diesen Monat"
          value={kpis ? formatDuration(kpis.thisMonthMinutes) : '–'}
          icon={Clock}
          color="bg-indigo-100 text-indigo-600"
          subtitle="Gesamte Abspielzeit"
        />
        <KPICard
          title="Dieses Jahr"
          value={kpis ? formatDuration(kpis.thisYearMinutes) : '–'}
          icon={BarChart2}
          color="bg-purple-100 text-purple-600"
          subtitle="Gesamte Abspielzeit"
        />
        <KPICard
          title="Meist abgespielt"
          value={kpis?.mostPlayed ? kpis.mostPlayed.title_de : '–'}
          icon={TrendingUp}
          color="bg-pink-100 text-pink-600"
          subtitle={kpis?.mostPlayed ? `${kpis.mostPlayed.count}x abgespielt` : 'Noch keine Daten'}
        />
      </div>

      {/* Add Sound Form */}
      {showAddForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Neuen Sound hinzufügen</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name (Deutsch) *
              </label>
              <input
                type="text"
                value={formData.title_de}
                onChange={(e) => setFormData((f) => ({ ...f, title_de: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="z.B. Meeresrauschen"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name (Englisch) *
              </label>
              <input
                type="text"
                value={formData.title_en}
                onChange={(e) => setFormData((f) => ({ ...f, title_en: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="e.g. Ocean Waves"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sound-Datei (MP3/WAV) *
              </label>
              <input
                ref={soundInputRef}
                type="file"
                accept="audio/mpeg,audio/wav,audio/mp3"
                onChange={(e) =>
                  setFormData((f) => ({ ...f, soundFile: e.target.files?.[0] || null }))
                }
                className="hidden"
              />
              <button
                onClick={() => soundInputRef.current?.click()}
                className="w-full flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg hover:border-indigo-400 transition-colors text-sm text-gray-600"
              >
                <Music className="w-4 h-4" />
                {formData.soundFile ? formData.soundFile.name : 'Sound-Datei auswählen...'}
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bild *
              </label>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={(e) =>
                  setFormData((f) => ({ ...f, imageFile: e.target.files?.[0] || null }))
                }
                className="hidden"
              />
              <button
                onClick={() => imageInputRef.current?.click()}
                className="w-full flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg hover:border-indigo-400 transition-colors text-sm text-gray-600"
              >
                <ImageIcon className="w-4 h-4" />
                {formData.imageFile ? formData.imageFile.name : 'Bild auswählen...'}
              </button>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={() => {
                setShowAddForm(false);
                setFormData({ title_de: '', title_en: '', soundFile: null, imageFile: null });
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleAddSound}
              disabled={isUploading}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              {isUploading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Speichern
            </button>
          </div>
        </div>
      )}

      {/* Sounds List */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Alle Sounds ({sounds.length})
          </h3>
        </div>

        {isLoading ? (
          <div className="p-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-4 animate-pulse">
                <div className="w-12 h-12 bg-gray-200 rounded-full" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : sounds.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Moon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">Keine Sounds vorhanden</p>
            <p className="text-sm mt-1">Füge deinen ersten Sleep Sound hinzu.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {sounds.map((sound) => (
              <div
                key={sound.id}
                className={`flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors ${
                  !sound.is_active ? 'opacity-50' : ''
                }`}
              >
                {/* Drag Handle */}
                <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0 cursor-grab" />

                {/* Image */}
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border border-gray-200">
                  <img
                    src={getPublicUrl('SleepSoundImages', sound.image_path)}
                    alt={sound.title_de}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {sound.title_de}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    EN: {sound.title_en}
                  </p>
                </div>

                {/* Sort Order */}
                <span className="text-xs text-gray-400 px-2 py-1 bg-gray-100 rounded">
                  #{sound.sort_order}
                </span>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleActive(sound)}
                    className={`p-2 rounded-lg transition-colors ${
                      sound.is_active
                        ? 'text-emerald-600 hover:bg-emerald-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={sound.is_active ? 'Deaktivieren' : 'Aktivieren'}
                  >
                    {sound.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => handleDelete(sound)}
                    className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
